import { useState, useEffect } from 'react';
import { formatDate } from '@/lib/datetime';
import Link from '@/lib/siteLink';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, BookOpen, Terminal } from 'lucide-react';
import VikasNav from './VikasNav';
import VikasFooter from './VikasFooter';
import SubscribeBox from '@/components/SubscribeBox';
import { VIKAS_SOCIALS, VIKAS_SAME_AS } from './vikasSocials';
import PlatformIcon from '@/components/PlatformIcon';
import { VIKAS_SUBJECT_OF } from './vikasPress';
import VikasPressStrip from './VikasPressStrip';

import AdSlot from '@/components/AdComponents';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VikasHome() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/content/articles?site=vikasifications&status=published&lightweight=true&exclude_sponsored=true&limit=3`)
      .then((r) => r.json())
      .then((data) => setPosts(data.articles || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Vikasifications, Vikas Goel | Founder, Author, Speaker</title>
        <meta name="description" content="Essays, books, and experiments for founders building without a Plan B. By Vikas Goel, founder of XO, DGTG, author of OMG I Can Do It." />
        <meta property="og:title" content="Vikasifications, Vikas Goel" />
        <meta property="og:description" content="Essays, books, and experiments for founders building without a Plan B." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.vikasifications.com" />
        <meta property="og:image" content="https://www.vikasifications.com/vikas-photo.jpg" />
        <link rel="canonical" href="https://www.vikasifications.com" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Person",
          "name": "Vikas Goel", "url": "https://www.vikasifications.com",
          "image": "https://www.vikasifications.com/vikas-photo.jpg",
          "jobTitle": "Founder & CEO",
          "worksFor": [
            { "@type": "Organization", "name": "XO (BattleXO)", "url": "https://www.battlexo.com", "description": "Game-tech operating system for emerging markets" },
            { "@type": "Organization", "name": "DGTG Marketing & Retail Pvt Ltd", "url": "https://www.dgtg.in", "description": "Community-powered digital growth agency" }
          ],
          "alumniOf": { "@type": "Organization", "name": "Startup Ecosystem India" },
          "sameAs": VIKAS_SAME_AS,
          "description": "Founder, author, speaker and builder. Building XO, the game-tech OS for emerging markets, and a ten-site network built with AI agents. Author of OMG I Can Do It.",
          "knowsAbout": ["Startups", "Gaming", "Esports", "Vibe coding", "AI agents", "Marketing", "Growth Strategy", "Game Tech", "AI"],
          "award": "Recognised among India's Top Builders, VibeCon (Emergent and Lightspeed), 2026",
          "subjectOf": VIKAS_SUBJECT_OF,
          "hasOccupation": [
            { "@type": "Occupation", "name": "Startup CEO", "description": "Leading XO - a game-tech platform" },
            { "@type": "Occupation", "name": "Author", "description": "Published OMG I Can Do It - startup journey book" },
            { "@type": "Occupation", "name": "Speaker", "description": "TEDx speaker, conference keynotes on startups, gaming, AI" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "WebSite",
          "name": "Vikasifications", "url": "https://www.vikasifications.com",
          "author": { "@type": "Person", "name": "Vikas Goel" },
          "description": "Essays, books, and experiments for founders building without a Plan B."
        })}</script>
      </Helmet>

      <VikasNav />

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-16" data-testid="vikas-hero">
        <div className="flex flex-col md:flex-row items-start gap-10">
          <img src="/vikas-photo.jpg" alt="Vikas Goel" className="w-40 h-40 rounded-full object-cover" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-black leading-tight">Hi, I&apos;m Vikas Goel</h1>
            <p className="text-black text-sm mt-3 leading-relaxed max-w-lg">
              Founder, author and speaker, currently spending most days vibecoding: building real products
              with AI agents rather than writing the code myself. Ten live sites, one pair of hands.
            </p>
            <p className="text-black text-sm mt-4 leading-relaxed max-w-lg">
              I am still learning this in public, which is the point. I publish what shipped, what broke and
              what it cost, so the mistakes are useful to someone other than me.
            </p>
            <p className="text-black text-sm mt-4 leading-relaxed max-w-lg">
              I founded <a href="https://www.battlexo.com" target="_blank" rel="noopener noreferrer" className="text-black font-medium hover:underline">XO</a>, a game-tech operating system for emerging markets, and <a href="https://www.dgtg.in" target="_blank" rel="noopener noreferrer" className="text-black font-medium hover:underline">DGTG</a>, a digital marketing agency, and have invested in over a dozen startups.
            </p>
            <div className="flex flex-wrap gap-4 mt-6 items-center">
              {VIKAS_SOCIALS.map((s) => (
                <a key={s.href} href={s.href} target="_blank" rel="me noopener noreferrer" aria-label={`Vikas Goel on ${s.label}`} title={s.label}
                  className="text-black/70 hover:text-black transition-colors"><PlatformIcon name={s.icon} className="w-5 h-5" /></a>
              ))}
              <a href="mailto:hi@vikasifications.com" className="text-sm text-black font-medium hover:underline">
                hi@vikasifications.com
              </a>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/vikas/build-log" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-black text-white text-sm font-semibold hover:bg-black/85">
                <Terminal className="w-4 h-4" /> Read the build notes
              </Link>
              <Link to="/vikas/work-with-me" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-gray-300 text-black text-sm font-semibold hover:border-black">
                Founders: send me your startup
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What I'm Doing Now */}
      <section className="max-w-3xl mx-auto px-6 py-12 border-t border-gray-100">
        <h2 className="text-[10px] font-bold text-black uppercase tracking-widest mb-6">What I&apos;m Doing Now</h2>
        <ul className="space-y-3 text-sm text-black">
          <li className="flex items-start gap-2"><span className="w-1 h-1 bg-black rounded-full mt-2 shrink-0" /> Running a ten-site network built and maintained almost entirely with AI agents</li>
          <li className="flex items-start gap-2"><span className="w-1 h-1 bg-black rounded-full mt-2 shrink-0" /> Publishing the build log: shipped work, hours, costs and the things that broke</li>
          <li className="flex items-start gap-2"><span className="w-1 h-1 bg-black rounded-full mt-2 shrink-0" /> Scaling XO across MENA &amp; KSA; doubling down on AI-led creator tools</li>
          <li className="flex items-start gap-2"><span className="w-1 h-1 bg-black rounded-full mt-2 shrink-0" /> Tearing down a few founders’ startups each month, free, for the pattern data</li>
        </ul>
      </section>

      {/* VibeCon recognition. A credential is worth showing once, with the thing it was given for. */}
      <section className="max-w-3xl mx-auto px-6 py-12 border-t border-gray-100" data-testid="vikas-credential">
        <div className="flex flex-col sm:flex-row items-start gap-8">
          <img src="/vibecon-top-builder.jpg" alt="VibeCon certificate recognising Vikas Goel among India's Top Builders, issued by Emergent with Lightspeed"
            className="w-full sm:w-64 rounded-lg border border-gray-200" loading="lazy" width="1200" height="849" />
          <div>
            <h2 className="text-[10px] font-bold text-black uppercase tracking-widest mb-2">Recognition</h2>
            <h3 className="text-xl font-serif font-bold text-black">Among India&apos;s Top Builders, VibeCon 2026</h3>
            <p className="text-sm text-black mt-2 leading-relaxed">
              Recognised by Emergent, with Lightspeed, for what I build rather than what I plan to build.
              I am treating it as a reason to publish more of the work, not as a finish line.
            </p>
            <Link to="/vikas/build-log" className="inline-flex items-center gap-1 text-sm text-black font-medium mt-4 hover:underline">
              See what the work actually looks like <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      <VikasPressStrip />

      {/* Book Teaser */}
      <section className="max-w-3xl mx-auto px-6 py-12 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row items-start gap-8">
          <img src="/book-omg.jpg" alt="OMG I Can Do It" className="w-32 rounded-lg shadow-md" />
          <div>
            <h2 className="text-[10px] font-bold text-black uppercase tracking-widest mb-2">My Book</h2>
            <h3 className="text-xl font-serif font-bold text-black">OMG... I Can Do It!</h3>
            <p className="text-sm text-black mt-2 leading-relaxed">A raw, honest journey of startup failures, resilience, and lessons. Not a playbook, lived reality.</p>
            <Link to="/vikas/book" className="inline-flex items-center gap-1 text-sm text-black font-medium mt-4 hover:underline">
              <BookOpen className="w-4 h-4" /> Read More <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="max-w-3xl mx-auto px-6 py-8 border-t border-gray-100">
        <AdSlot site="vikas" placement="home_mid" maxAds={1} />
      </section>

      {/* Latest Posts */}
      <section className="max-w-3xl mx-auto px-6 py-12 border-t border-gray-100" data-testid="vikas-posts">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-[10px] font-bold text-black uppercase tracking-widest">Latest Writing</h2>
          <Link to="/vikas/blog" className="text-sm text-black hover:underline flex items-center gap-1">All Essays <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {posts.length > 0 ? (
          <div className="space-y-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/vikas/blog/${p.slug || p.id}`} className="block group" data-testid={`post-${p.id}`}>
                <h3 className="text-lg font-serif font-bold text-black group-hover:underline">{p.title}</h3>
                {p.excerpt && <p className="text-sm text-black mt-1 line-clamp-2">{p.excerpt}</p>}
                {p.updated_at && <p className="text-xs text-black mt-2 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(p.updated_at)}</p>}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-black">Essays coming soon.</p>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16">
        <SubscribeBox site="vikasifications" source="vikas-home" />
      </section>

      <VikasFooter />
    </div>
  );
}
